import { useState, useCallback } from 'react';
import { produce, Draft, freeze } from 'immer';

export type Updater<S> = (draft: Draft<S>) => void;

export type IValueOrUpdater<State> = Updater<State> | State;

export type SetterOrUpdaterImmer<S> = (valueOrUpdater: IValueOrUpdater<S>) => void;

const useImmutableState = <S = unknown>(
    initialState: S | (() => S),
): [S, SetterOrUpdaterImmer<S>] => {
    const [state, setState] = useState<S>(
        freeze<S>(
            typeof initialState === 'function' ? (initialState as () => S)() : initialState,
            true,
        ),
    );

    const updateState = useCallback((valueOrUpdater: IValueOrUpdater<S>): void => {
        if (typeof valueOrUpdater === 'function') {
            setState(produce<S>(valueOrUpdater as Updater<unknown>));
        } else {
            setState(freeze(valueOrUpdater));
        }
    }, []);

    return [state, updateState];
};

export default useImmutableState;
